// pages/FormViewerPage/hooks/useFormViewer/actionsSubmit.ts — Submit handler (thin)
import { useCallback, useContext } from 'react';
import { fireSubmitTracking } from './submitTracking';
import { runSubmitGuards } from './submitGuards';
import { runSubmitWithRetry } from './submitRetry';
import { usePerfThresholds } from '../../../../services/shippingPerf';
import { ToastContext } from '../../../../contexts/ToastContext';
import type { FormViewerState } from './state';
import type { FormViewerComputed } from './computed';

// Error validasi RPC (deterministik) → pesan ramah, no retry. Return null kalau
// bukan salah satu pola ini (berarti kemungkinan network/5xx → boleh di-retry).
// Diekspor untuk dipakai wizard step 1 (pesan ramah yang sama, tanpa duplikasi).
export function mapValidationError(message?: string): string | null {
  if (!message) return null;
  if (message.includes('customer wajib diisi')) return 'Nama lengkap wajib diisi.';
  if (message.includes('customerPhone wajib diisi')) return 'Nomor WhatsApp wajib diisi.';
  if (message.includes('formId wajib diisi')) return 'Form tidak valid, silakan muat ulang halaman.';
  if (message.includes('Alamat minimal')) return 'Alamat belum lengkap, mohon isi alamat lebih detail.';
  if (message.includes('Form tidak ditemukan')) return 'Form ini sudah tidak aktif atau tidak ditemukan.';
  if (message.includes('HARGA_BERUBAH')) return 'Harga produk di form berubah saat kamu checkout. Muat ulang halaman lalu pesan lagi ya.';
  if (message.includes('KURIR_DIBLOKIR')) return 'Kurir tersebut tidak mendukung produk ini. Silakan pilih kurir lain.';
  if (message.includes('COD_ONGKIR_MAHAL')) return 'COD tidak tersedia untuk ongkir di atas batas — silakan pilih Transfer Bank atau QRIS.';
  if (message.includes('WIZARD_FORBIDDEN')) return 'Pesanan ini sudah diproses sistem — menampilkan ringkasannya.';
  if (message.includes('PESANAN_DUPLIKAT')) return 'Kamu sudah punya pesanan produk ini yang sedang diproses. Kalau mau dibantu, hubungi admin ya.';
  return null;
}

export function useFormViewerSubmit(s: FormViewerState, c: FormViewerComputed, validateFields: () => Record<string, string>, isSimpleEmbed: boolean = false, isSeamlessEmbed: boolean = false, discountAmount: number = 0, bump?: { selectedBumpIds: string[]; bumpSum: number }) {
  const {
    form, customerData, addressData, selectedOptions, selectedShippingKey,
    selectedPaymentKey, selectedDuitkuMethod, selectedEverproRate, selectedBiteshipRate, linkedProductName,
    identifier, navigate, quantity, globalTrackingSettings, formPagePixels, formPagePixelConfigs,
    setFieldErrors, setIsSubmitting, setSubmission, hasSubmittedRef, submitLockRef,
  } = s;
  const { currentCombination, shippingCost, codFee } = c;
  const { minMid: COURIER_MID_PERFORMANCE } = usePerfThresholds();
  const showToast = useContext(ToastContext)?.showToast;

  const handleSubmit = useCallback(async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    const errs = validateFields();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      submitLockRef.current = false;
      // Notifikasi eksplisit + arahkan ke yang kurang (fix: submit mentok tanpa
      // feedback → customer bingung/abandon, khususnya varian belum dipilih).
      const variantMissing = Object.keys(errs).filter(k => k.startsWith('variant:')).map(k => k.replace('variant:', '').trim());
      showToast?.(
        variantMissing.length
          ? `Pilih dulu: ${variantMissing.join(', ')}.`
          : 'Lengkapi data yang wajib diisi dulu ya.',
        'error',
      );
      requestAnimationFrame(() => {
        const target = variantMissing.length
          ? document.querySelector('[data-variant-section]')
          : document.querySelector('form');
        (target as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    fireSubmitTracking({ form, currentCombination, quantity, identifier, formPagePixels, formPagePixelConfigs });
    setIsSubmitting(true);
    setFieldErrors({});

    const orderId = crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); });
    const uniqueCode = Math.min(100, Math.max(1, (Date.now() % 100000) % 100 + 1));
    const isCODVal = selectedPaymentKey ? ['cod', 'codJawaBali', 'codLuarJawaBali'].includes(selectedPaymentKey as string) : false;

    const guardErr = await runSubmitGuards({
      form, addressData, selectedPaymentKey, selectedDuitkuMethod,
      selectedEverproRate, selectedBiteshipRate, shippingCost, isCODVal, minMid: COURIER_MID_PERFORMANCE,
      brandEverpro: s.brandEverpro,
      brandMengantar: s.brandMengantar, brandLincah: (s as any).brandLincah,
        rateHold: !!(s.rateHoldRef?.current),
        ratesSettled: (s as any).ratesSettledRef?.current,
        lowPerfNoCod: !!(s.lowPerfNoCod),
      selectedShippingKey,
    });
    if (guardErr) {
      setSubmission({ success: false, error: guardErr });
      setIsSubmitting(false);
      submitLockRef.current = false;
      return;
    }

    await runSubmitWithRetry({
      s, c,
      builtParams: {
        form, customerData, addressData, selectedOptions, selectedShippingKey,
        selectedPaymentKey, selectedDuitkuMethod, selectedEverproRate, selectedBiteshipRate, currentCombination, linkedProductName,
        quantity, shippingCost, codFee, orderId, uniqueCode, isCODVal, isSimpleEmbed,
        herbalClaimMode: s.herbalClaimMode,
        discountAmount,
        voucherCode: s.couponApplied?.code || '',
        bumpProductIds: bump?.selectedBumpIds || [],
        bumpSum: bump?.bumpSum || 0,
      },
      rpcOpts: { isSimpleEmbed, isSeamlessEmbed },
      orderMeta: { form, identifier, navigate },
      setters: { setIsSubmitting, setSubmission, hasSubmittedRef, submitLockRef },
      mapValidationError,
    });
  }, [form, customerData, addressData, selectedOptions, selectedShippingKey, selectedPaymentKey, selectedDuitkuMethod, selectedEverproRate, selectedBiteshipRate, currentCombination, shippingCost, codFee, linkedProductName, validateFields, identifier, navigate, quantity, formPagePixels, formPagePixelConfigs, discountAmount, showToast, bump?.selectedBumpIds, bump?.bumpSum]);

  return { handleSubmit };
}
